import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputBase,
    Tooltip,
    Typography,
    Box,
    DialogActions,
    Popover,
} from '@mui/material';
import { EditOutlined, DeleteOutline, Done, Close, Lock, DragIndicator, InfoOutlined } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { STATUS_PALETTE, statusColor } from 'views/deal_flow_board/statusColors';
import { STATUS_ICONS, STATUS_ICON_LABELS, effectiveIconKey, statusIconComponent } from 'views/deal_flow_board/statusIcons';
import { capitalizedSentence } from 'utils/index';
import CreateNewPipelineStatusPopup from './CreateNewPipelineStatusPopup';
import CreateDealFlowPopup from './CreateNewDealFlowPopup';
import { CreateDealFlow, CreatePipeline, DeletePipelineFlow, UpdatePipelineStatusAppearance } from 'api/deal_flow';
import { DeletePipelineStatus, ReorderPipelines, ReorderStatuses } from 'api/startup_applications';
import { Form, FormikProvider, useFormik } from 'formik';
import DeleteConfirmationDialog from 'ui-components/popups/DeleteConfirmationPopup';
import { useToast } from 'context/ToastContext';

const INDIGO = '#444CE7';
const INDIGO_RING = 'rgba(68, 76, 231, 0.12)';
// ClickUp status-editor metrics: 32px rows, #E8E8E8 hairline, 13px labels; hover tints instead of lifting.
const ROW_SX = {
    display: 'flex', alignItems: 'center', gap: '8px',
    minHeight: 32, pl: '6px', pr: '4px', mb: '8px',
    border: '1px solid #E8E8E8', borderRadius: '8px', backgroundColor: '#fff',
    transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': { borderColor: '#D0D5DD', backgroundColor: '#FAFAFA' },
};
const HANDLE_SX = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 24, color: '#B9B9B9', flexShrink: 0 };
const LABEL_SX = { flex: 1, minWidth: 0, fontSize: '0.8125rem', fontWeight: 500, color: '#202020', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const ACTION_SX = { width: 24, height: 24, borderRadius: '6px', color: '#838383', '& svg': { fontSize: 16 }, '&:hover': { backgroundColor: '#F0F0F0', color: '#202020' } };
const SECTION_SX = { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', fontWeight: 500, color: '#838383', mb: '8px' };

// Icon option in the status popover: 28px, selected = soft grey well with an indigo ring.
const iconButtonSx = (selected, glyphColor) => ({
    width: 28, height: 28, p: 0, border: 'none', borderRadius: '6px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: glyphColor,
    backgroundColor: selected ? '#F0F0F0' : 'transparent',
    boxShadow: selected ? `inset 0 0 0 1.5px ${INDIGO}` : 'none',
    '&:hover': { backgroundColor: '#F0F0F0' },
    '&:focus-visible': { outline: `2px solid ${INDIGO}`, outlineOffset: 1 },
});

const SettingsPopup = ({
    open = false,
    handleClose = () => { },
    mainTabValue = "All",
    tabValue = null,
    pipelineTabs = [],
    statusTabs = [],
    onEditPipeline = () => { },
    onEditStatus = () => { },
    // onDeletePipeline = () => { },
    handleDeletePipeline = () => { },
    handleDeleteStatus = () => { },
    // onDeletePipelineStatus = () => { },
    handleReorderPipelines = () => { },
    handleReorderStatuses = () => { },
    onChangeStatusAppearance = () => { },
    refetchPipelineTabs,
    refetchStatusTabs,
    settingsType,
    setMainTabValue,
    setValue,
    investor_id,
    isLoading,
    ...props
}) => {

    const { addToast } = useToast();
    // Derive fixed columns dynamically based on is_fixed boolean
    const fixedStatusColumns = statusTabs
        .filter(item => item?.is_fixed === true)
        .map(item => item?.label);
    const fixedPipelinesColumns = pipelineTabs
        .filter(item => item?.is_fixed === true)
        .map(item => item?.label);

    const { mutateAsync: updatePipelinesDisplayOrder, isLoading: isReorderingPipelines } = ReorderPipelines();

    const onReorderPipelines = (props) => {
        const reorderedItems = props?.flatMap((item) => {
            if (!fixedPipelinesColumns.includes(item?.label)) {
                return [{ pipelineID: item.value, display_order: item?.display_order || 0 }];
            }
            return [];
        });

        updatePipelinesDisplayOrder({
            payload: [...reorderedItems],
            investor_id
        }, {
            onSuccess: () => {
                setTimeout(() => {
                    // console.log(props)
                    handleReorderPipelines(props?.filter(item => item?.value !== "All"))
                    // refetchPipelineTabs()
                }, 0)
            },
            onError: () => { }
        });
    };

    const { mutateAsync: updateStatusDisplayOrder, isLoading: isReorderingStatus } = ReorderStatuses();

    const onReorderStatus = (props, updateArray) => {
        const reorderedItems = props?.map((item) => {
            return { "statusID": item?.value, "display_order": item?.display_order };
        });

        updateStatusDisplayOrder({
            payload: [...reorderedItems],
            investor_id
        }, {
            onSuccess: () => {
                setTimeout(() => {
                    //  console.log(prop2)
                    handleReorderStatuses(updateArray)
                    // refetchStatusTabs()
                }, 0)
            },
            onError: () => { }
        });
    };

    const formik = useFormik({
        initialValues: {
            statuses: statusTabs.map(item => {
                return {
                    ...item,
                    label: item?.label,
                    defaultLabel: item?.label,
                    value: item?.value,
                    display_order: item?.display_order
                }
            }),
            pipelines: pipelineTabs.map(item => {
                return {
                    ...item,
                    label: item?.label,
                    defaultLabel: item?.label,
                    value: item?.value,
                    display_order: item?.display_order
                }
            })
        }
    })

    // Popup states 

    const [currentRow, setCurrentRow] = React.useState(null);

    const { mutateAsync: deletePiplineFlow, isLoading: isDeletingPipelineFlow } = DeletePipelineFlow({investor_id})
    const { mutateAsync: deletePipelineStatus, isLoading: isDeletingPipelineStatus } = DeletePipelineStatus({ investor_id })

    const [openDeletePipelineFlowPopup, setOpenDeletePipelineFlowPopup] = React.useState(false)
    const [openDeletePipelineStatusPopup, setOpenDeletePipelineStatusPopup] = React.useState(false)

    const onDeletePipelineStatus = ({ _id, name }) => {
        // setCurrentRow();
        setCurrentRow({ _id: _id, name });
        setOpenDeletePipelineStatusPopup(true)
    }

    const onDeletePipeline = ({ _id, name }) => {
        // setCurrentRow();
        setCurrentRow({ _id: _id, name });
        setOpenDeletePipelineFlowPopup(true)
    }


    const handleCloseDeletePipelineFlowPopup = () => {
        setOpenDeletePipelineFlowPopup(false);
        setCurrentRow(null);
    }

    const handleCloseDeletePipelineStatusPopup = () => {
        setOpenDeletePipelineStatusPopup(false);
        setCurrentRow(null);
    }

    const handleDeletePipelineFlow = () => {
        // console.log(currentRow, "delete");
        deletePiplineFlow({ pipeline_id: currentRow?._id }, {
            onSuccess: (response) => {
                if (response.data.statusCode === 200) {
                    // refetchListing()
                    handleDeletePipeline(formik.values.pipelines.filter(item => item?.value !== currentRow?._id))
                    formik.setFieldValue("pipelines", formik.values.pipelines.filter(item => item?.value !== currentRow?._id))
                    // formik.setFieldValue("pipelines", statusTabs.find(item => item?.value !== currentRow?._id))
                    // refetchPipelineTabs()
                    mainTabValue === currentRow?._id && setMainTabValue("All");
                    handleCloseDeletePipelineFlowPopup();
                    addToast("Pipeline Deleted!", "success")
                }
                else {
                    addToast(response.data.message, "error")
                }
            },
            onError: () => {
                // handleCloseDeletePipelineFlowPopup()
            }
        })
    }

    const handleDeletePipelineStatus = () => {
        // console.log({ statusId: currentRow?._id }, "currentRow")
        deletePipelineStatus({ statusId: currentRow?._id }, {
            onSuccess: (response) => {
                if (response.data.statusCode === 200) {
                    // refetchListing()
                    handleDeleteStatus(formik.values.statuses.filter(item => item?.value !== currentRow?._id))
                    formik.setFieldValue("statuses", formik.values.statuses.filter(item => item?.value !== currentRow?._id))
                    // formik.setFieldValue("statuses", statusTabs.find(item => item?.value !== currentRow?._id))
                    // refetchStatusTabs()
                    handleCloseDeletePipelineStatusPopup();
                    addToast("Status Deleted!", "success")
                }
                else {
                    addToast(response.data.message, "error")
                }
            },
            onError: () => {
                //handleCloseDeletePipelineStatusPopup()
            }
        })
    }

    const [editMode, setEditMode] = useState({ type: '', id: null });
    const [editText, setEditText] = useState('');
    const [closeText, setCloseText] = useState('');

    const handleEditClick = (type, item, initVal) => {
        formik.setFieldValue(initVal, initVal === "pipelines" ? pipelineTabs.map(item => {
                return {
                    ...item,
                    label: item?.label,
                    defaultLabel: item?.label,
                    value: item?.value,
                    display_order: item?.display_order
                }
            }) : statusTabs.map(item => {
                return {
                    ...item,
                    label: item?.label,
                    defaultLabel: item?.label,
                    value: item?.value,
                    display_order: item?.display_order
                }
            }) )
        setEditMode({ type, id: item.value });
        setEditText(item.label);
        setCloseText(item?.label)
    };

    const handleEditClose = ({ index, initVal }) => {
        formik.setFieldValue(`${initVal}.${index}.label`, closeText);
        setEditMode({ type: '', id: null });
        setCloseText('')
    };

    const handleSave = () => {
        if (editMode.type === 'pipeline') {
            onEditPipeline({ pipeline_id: editMode.id, label: capitalizedSentence(editText.trim()) });
        } else if (editMode.type === 'status') {
            onEditStatus({ status_id: editMode.id, label: capitalizedSentence(editText.trim()) });
        }
        setEditMode({ type: '', id: null });
    };

    const { mutateAsync: createNewPiplineFlow, isLoading: isCreatingPipeline } = CreatePipeline({ investor_id: investor_id });
    const [openCreatePipelinePopup, setOpenCreatePipelinePopup] = React.useState(false);

    const handleCloseCreatePipelinePopup = () => {
        setOpenCreatePipelinePopup(false);
    };

    const handleOpenCreatePipelinePopup = () => {
        setOpenCreatePipelinePopup(true);
    };

    const handleSubmissionCreatePipline = ({ payload }) => {
        createNewPiplineFlow({ ...payload }, {
            onSuccess: () => {
                // refetchPipelineTabs();
                handleCloseCreatePipelinePopup();
            },
            onError: () => {
                // refetchPipelineTabs();
            }
        });
    };

    const { mutateAsync: createNewDealFlow, isLoading: isCreatingDealFlow } = CreateDealFlow();
    const [openCreateDealPopup, setOpenCreateDealPopup] = React.useState(false);

    const handleCloseCreateDealPopup = () => {
        setOpenCreateDealPopup(false);
    };

    const handleOpenCreateDealPopup = () => {
        setOpenCreateDealPopup(true);
    };

    const handleSubmissionCreateDealFlow = ({ payload }) => {
        createNewDealFlow({ ...payload, pipeline_id: mainTabValue }, {
            onSuccess: () => {
                // refetchStatusTabs();
                handleCloseCreateDealPopup();
            },
            onError: () => {
                // refetchStatusTabs();
            }
        });
    };

    const handleDragEnd = (result, type, items) => {
        if (!result.destination) return;

        if (type === 'pipeline') {
            const _items = [...items];
            const [reorderedItem] = _items.splice(result.source.index, 1);
            _items.splice(result.destination.index, 0, reorderedItem);

            const updatedItems = _items.map((item, index) => ({
                ...item,
                display_order: index
            }));

            const fixedElement = formik.values.pipelines?.find(item => fixedPipelinesColumns.includes(item?.label));
            const _updatedItems = fixedElement ? [{ ...fixedElement, display_order: null }, ...updatedItems] : updatedItems;

            formik.setFieldValue("pipelines", _updatedItems);
            onReorderPipelines(_updatedItems);
        } else {
            const _items = [...items];
            const [reorderedItem] = _items.splice(result.source.index, 1);
            _items.splice(result.destination.index, 0, reorderedItem);

            const updatedItems = _items.map((item, index) => ({
                ...item,
                display_order: index + 1
            }));

            const newStatus = formik.values.statuses?.find(item => item?.label === fixedStatusColumns.find(label => label === "New"));
            const rejectedStatus = formik.values.statuses?.find(item => item?.label === fixedStatusColumns.find(label => label === "Rejected"));

            const _updatedItems = [
                ...(newStatus ? [{ ...newStatus, display_order: 0 }] : []),
                ...updatedItems,
                ...(rejectedStatus ? [{ ...rejectedStatus, display_order: 99 }] : [])
            ];

            formik.setFieldValue("statuses", _updatedItems);
            onReorderStatus(updatedItems, _updatedItems); // Only send non-fixed items for reordering
        }
    };

    // Color + icon picker for status rows: every status can change both, including the fixed New and Rejected.
    const { mutateAsync: updateStatusAppearance } = UpdatePipelineStatusAppearance();
    const EMPTY_PICKER = { anchor: null, itemId: null, draft: null };
    const [picker, setPicker] = useState(EMPTY_PICKER);
    // Read the row from formik so the popover reflects a change the moment it is made.
    const pickerItem = picker.itemId ? formik.values.statuses?.find((s) => s?.value === picker.itemId) : null;

    // Saves one or more fields (color and/or icon) of a status; rolls the row back if the API refuses.
    const saveStatusAppearance = (item, patch) => {
        const previous = Object.fromEntries(Object.keys(patch).map((key) => [key, item?.[key] ?? null]));
        const setFields = (fields) => formik.setFieldValue('statuses', formik.values.statuses.map((s) => (s?.value === item?.value ? { ...s, ...fields } : s)));

        setFields(patch);
        updateStatusAppearance({ investorId: investor_id, statusId: item?.value, ...patch }, {
            onSuccess: (response) => {
                if (response?.data?.statusCode === 200) {
                    onChangeStatusAppearance({ status_id: item?.value, ...patch });
                } else {
                    setFields(previous);
                    addToast(response?.data?.message || 'Could not update the status', 'error');
                }
            },
            onError: () => {
                setFields(previous);
                addToast('Could not update the status', 'error');
            },
        });
    };

    // A swatch finishes at once; the custom color picker commits on close. The popover stays open for icons.
    const finishColorPicker = (color) => {
        const item = pickerItem;
        setPicker(EMPTY_PICKER);
        if (!item || !color) return;
        const current = statusColor({ ...item, value: item?.mappedValue ?? item?.value });
        if (color.toUpperCase() !== current.toUpperCase()) saveStatusAppearance(item, { color });
    };

    const chooseIcon = (key) => {
        if (pickerItem && effectiveIconKey(pickerItem) !== key) saveStatusAppearance(pickerItem, { icon: key });
    };

    const openColorPicker = (event, item) => {
        setPicker({ anchor: event.currentTarget, itemId: item?.value, draft: statusColor({ ...item, value: item?.mappedValue ?? item?.value }) });
    };

    // Status rows carry the board's lane colour (keyed by mappedValue, e.g. 'invited') and optional icon; pipelines have neither.
    const dot = (item) => {
        if (settingsType !== 'status') return false;
        const color = statusColor({ ...item, value: item?.mappedValue ?? item?.value });
        const Icon = statusIconComponent(item);
        return (
            <Box
                component="button"
                type="button"
                aria-label={`Change color and icon of ${item?.label}`}
                onClick={(e) => openColorPicker(e, item)}
                sx={{
                    width: Icon ? 16 : 12, height: Icon ? 16 : 12, p: 0, border: 'none', flexShrink: 0, cursor: 'pointer',
                    borderRadius: Icon ? 0 : '50%', backgroundColor: Icon ? 'transparent' : color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color,
                    '& svg': { fontSize: 16 },
                    '&:hover': { opacity: 0.8 },
                    '&:focus-visible': { outline: `2px solid ${INDIGO}`, outlineOffset: 2 },
                }}
            >
                {Icon && <Icon />}
            </Box>
        );
    };

    const renderFixedItem = (item) => (
        <Box key={item.value} sx={ROW_SX}>
            <Tooltip title={`${item?.label} is fixed - it can't be moved or renamed`} placement="left">
                <Box sx={HANDLE_SX}><Lock sx={{ fontSize: 13 }} /></Box>
            </Tooltip>
            {dot(item)}
            <Typography sx={{ ...LABEL_SX, ...(settingsType === 'status' && { textTransform: 'uppercase' }) }}>{capitalizedSentence(item?.label)}</Typography>
        </Box>
    );

    const renderList = (type, items, onDelete, initVal) => (
        <DragDropContext onDragEnd={(result) => handleDragEnd(result, type, items)}>
            <Droppable droppableId={`${type}-list`} mode='standard'>
                {(provided) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
                    >
                        {items.map((item, index) => {
                            const isEditing = editMode.type === type && editMode.id === item.value;
                            return (
                                <Draggable key={item.value} draggableId={item.value} index={index} isDragDisabled={isEditing}>
                                    {(provided, snapshot) => (
                                        <Box
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            sx={{
                                                ...ROW_SX,
                                                ...(isEditing && { borderColor: INDIGO, boxShadow: `0 0 0 3px ${INDIGO_RING}` }),
                                                ...(snapshot.isDragging && { boxShadow: '0 8px 20px rgba(16,24,40,0.14)' }),
                                                '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
                                            }}
                                        >
                                            <Box sx={{ ...HANDLE_SX, cursor: 'grab' }} aria-label={`Drag to reorder ${item?.label}`} {...provided.dragHandleProps}>
                                                <DragIndicator sx={{ fontSize: 14 }} />
                                            </Box>
                                            {dot(item)}
                                            {isEditing ? (
                                                <InputBase
                                                    autoFocus
                                                    value={item.label}
                                                    onChange={(e) => {
                                                        const currentVal = e.target.value;
                                                        if (currentVal?.length <= 15) {
                                                            formik.setFieldValue(`${initVal}.${index + 1}.label`, capitalizedSentence(currentVal).replace(/[^a-zA-Z0-9_\-\s]/g, ''))
                                                            setEditText(
                                                                capitalizedSentence(currentVal).replace(/[^a-zA-Z0-9_\-\s]/g, '')
                                                            );
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
                                                        if (e.key === 'Escape') { e.stopPropagation(); handleEditClose({ index: index + 1, initVal }); }
                                                    }}
                                                    inputProps={{ 'aria-label': `Rename ${item?.label}`, maxLength: 15 }}
                                                    sx={{ flex: 1, fontSize: '0.8125rem', fontWeight: 500, color: '#202020', '& input': { p: 0 } }}
                                                />
                                            ) : (
                                                <Typography sx={{ ...LABEL_SX, ...(settingsType === 'status' && { textTransform: 'uppercase' }) }}>{item.label}</Typography>
                                            )}
                                            <Box className="row-actions" sx={{ display: 'flex', alignItems: 'center', gap: '2px', ml: 'auto', opacity: isEditing ? 1 : 0, transition: 'opacity 0.15s ease', '@media (hover: none)': { opacity: 1 } }}>
                                                {isEditing ? (
                                                    <>
                                                        <Tooltip title="Save"><IconButton aria-label="Save" sx={{ ...ACTION_SX, color: INDIGO }} onClick={() => handleSave()}><Done /></IconButton></Tooltip>
                                                        <Tooltip title="Cancel"><IconButton aria-label="Cancel" sx={ACTION_SX} onClick={() => handleEditClose({ index: index + 1, initVal })}><Close /></IconButton></Tooltip>
                                                    </>
                                                ) : (
                                                    <>
                                                        {item?.is_editable && (
                                                            <Tooltip title="Rename"><IconButton aria-label={`Rename ${item?.label}`} sx={ACTION_SX} onClick={() => handleEditClick(type, item, initVal)}><EditOutlined /></IconButton></Tooltip>
                                                        )}
                                                        {item?.is_deletable && (
                                                            <Tooltip title="Delete">
                                                                <IconButton
                                                                    aria-label={`Delete ${item?.label}`}
                                                                    sx={{ ...ACTION_SX, '&:hover': { backgroundColor: '#FEF3F2', color: '#D92D20' } }}
                                                                    onClick={() => onDelete({ _id: item.value, name: item?.label })}
                                                                >
                                                                    <DeleteOutline />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        </DragDropContext>
    );

    React.useEffect(() => {
        if (settingsType === "pipeline") {
            if (formik.values.pipelines?.length < 1) {
                setMainTabValue("All");
                handleClose();
            }
        }
        // else if (settingsType === "status") {
        //     if (statuses?.length < 1) {
        //         // setValue("All");
        //         handleClose();
        //     }
        // }
    }, [formik.values.pipelines?.length, formik.values.statuses?.length, settingsType]);

    return (
        <>
            {openCreatePipelinePopup && (
                <CreateNewPipelineStatusPopup
                    handleClose={handleCloseCreatePipelinePopup}
                    open={openCreatePipelinePopup}
                    handleSubmission={handleSubmissionCreatePipline}
                    isCreating={isCreatingPipeline}
                />
            )}
            {openCreateDealPopup && (
                <CreateDealFlowPopup
                    handleClose={handleCloseCreateDealPopup}
                    open={openCreateDealPopup}
                    handleSubmission={handleSubmissionCreateDealFlow}
                    isCreating={isCreatingDealFlow}
                />
            )}

            {openDeletePipelineStatusPopup && <DeleteConfirmationDialog open={openDeletePipelineStatusPopup} handleClose={handleCloseDeletePipelineStatusPopup} handleSubmission={handleDeletePipelineStatus} heading={"Delete Status"} ActionText={"Delete"} subheading={`Are you sure you want to delete this status? Startups from this status are accesible under the "All" tab.`} maxWidth="xs" />
            }
            {openDeletePipelineFlowPopup && <DeleteConfirmationDialog open={openDeletePipelineFlowPopup} handleClose={handleCloseDeletePipelineFlowPopup} handleSubmission={handleDeletePipelineFlow} heading={"Delete Pipeline"} ActionText={"Delete"} subheading={`Are you sure you want to delete this pipeline? Startups from this pipeline are accesible under the "All" tab.`} maxWidth="xs" />
            }

            <Popover
                open={Boolean(picker.anchor)}
                anchorEl={picker.anchor}
                onClose={() => finishColorPicker(picker.draft)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: '12px', width: 224, borderRadius: '8px', boxShadow: '0 8px 24px rgba(16,24,40,0.14)' } }}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
                    {STATUS_PALETTE.map((hex) => (
                        <Box
                            key={hex}
                            component="button"
                            type="button"
                            aria-label={`Set color ${hex}`}
                            onClick={() => finishColorPicker(hex)}
                            sx={{
                                width: 24, height: 24, p: 0, border: 'none', borderRadius: '50%', cursor: 'pointer', backgroundColor: hex,
                                boxShadow: picker.draft?.toUpperCase() === hex ? `0 0 0 2px #fff, 0 0 0 4px ${hex}` : 'none',
                            }}
                        />
                    ))}
                </Box>
                <Box sx={{ mt: '12px', pt: '12px', borderTop: '1px solid #E8E8E8', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', fontWeight: 500, color: '#646464' }}>
                    <Box component="label" sx={{ position: 'relative', width: 24, height: 24, borderRadius: '50%', overflow: 'hidden', cursor: 'pointer', backgroundColor: picker.draft, border: '1px solid #E8E8E8', flexShrink: 0 }}>
                        <input
                            type="color"
                            aria-label="Pick a custom color"
                            value={picker.draft || '#000000'}
                            onChange={(e) => setPicker((prev) => ({ ...prev, draft: e.target.value }))}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, border: 0, padding: 0, cursor: 'pointer' }}
                        />
                    </Box>
                    Custom color
                </Box>
                <Box sx={{ mt: '12px', pt: '12px', borderTop: '1px solid #E8E8E8' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#646464', mb: '8px' }}>Icon</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px' }}>
                        <Tooltip title="No icon" placement="top">
                            <Box
                                component="button"
                                type="button"
                                aria-label="No icon"
                                aria-pressed={!effectiveIconKey(pickerItem)}
                                onClick={() => chooseIcon('')}
                                sx={iconButtonSx(!effectiveIconKey(pickerItem), '#646464')}
                            >
                                <Close sx={{ fontSize: 14 }} />
                            </Box>
                        </Tooltip>
                        {Object.entries(STATUS_ICONS).map(([key, Icon]) => (
                            <Tooltip key={key} title={STATUS_ICON_LABELS[key] || key} placement="top">
                                <Box
                                    component="button"
                                    type="button"
                                    aria-label={`Set icon ${STATUS_ICON_LABELS[key] || key}`}
                                    aria-pressed={effectiveIconKey(pickerItem) === key}
                                    onClick={() => chooseIcon(key)}
                                    sx={iconButtonSx(effectiveIconKey(pickerItem) === key, statusColor({ ...pickerItem, value: pickerItem?.mappedValue ?? pickerItem?.value }))}
                                >
                                    <Icon sx={{ fontSize: 16 }} />
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                </Box>
            </Popover>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                {...props}
                sx={{
                    ...props.sx,
                    "& > .MuiDialog-container > .MuiPaper-root": {
                        borderRadius: "12px",
                        overflow: 'hidden'
                    }
                }}
            >
                {/* ClickUp-style header: compact title, hairline divider, small round close. */}
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '16px 16px 16px 24px', borderBottom: '1px solid #E8E8E8' }}>
                    <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: '24px', color: '#202020' }}>
                        Manage {settingsType === "pipeline" ? "Pipelines" : settingsType === "status" ? "Status" : ""}
                    </Typography>
                    <IconButton aria-label="Close" onClick={handleClose} sx={{ width: 28, height: 28, backgroundColor: '#F0F0F0', color: '#646464', '& svg': { fontSize: 16 }, '&:hover': { backgroundColor: '#E8E8E8', color: '#202020' } }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ overflow: 'hidden', p: '16px 24px 16px !important' }}>
                    <FormikProvider value={formik}>
                        <Form onSubmit={() => { }}>
                            {<>
                                {settingsType === "pipeline" && (
                                    <Box mb={0}>
                                        <Box sx={SECTION_SX}>Pipelines</Box>
                                        {formik.values.pipelines?.filter(item => fixedPipelinesColumns.includes(item?.label)).map(renderFixedItem)}
                                        {renderList('pipeline', formik.values.pipelines?.filter(item => !fixedPipelinesColumns.includes(item?.label)), onDeletePipeline, "pipelines")}
                                    </Box>
                                )}
                                {settingsType === "status" && (
                                    <Box>
                                        <Box sx={SECTION_SX}>
                                            Statuses
                                            <Tooltip title="New and Rejected are fixed. Drag the others to reorder.">
                                                <InfoOutlined sx={{ fontSize: 14, color: '#B9B9B9' }} />
                                            </Tooltip>
                                        </Box>
                                        {formik.values.statuses?.filter(item => item?.label === fixedStatusColumns.find(label => label === "New")).map(renderFixedItem)}
                                        {renderList('status', formik.values.statuses?.filter(item => !fixedStatusColumns.includes(item?.label)), onDeletePipelineStatus, "statuses")}
                                        {formik.values.statuses?.filter(item => item?.label === fixedStatusColumns.find(label => label === "Rejected")).map(renderFixedItem)}
                                    </Box>
                                )}

                            </>}
                        </Form>
                    </FormikProvider>
                    {/* <Box sx={{
                        paddingTop: '0.5rem',
                    }}>
                        <Typography
                            sx={{
                                fontSize: "0.8rem",
                                fontWeight: 400,
                                lineHeight: "24px",
                                color: theme => theme.palette.customColors.g200,
                            }}
                        >
                            Drag to reorder. Click to edit or delete.
                        </Typography>
                    </Box> */}

                </DialogContent>
            </Dialog>
        </>
    );
};

export default SettingsPopup;

